// src/components/PrivateRoute.js
//
// Backwards compatible with every existing call site:
//   <PrivateRoute allowedRoles={[...]} currentUserRole={currentUserRole}>
//
// Two optional props are new:
//   allowFree          — this route doesn't require a granted role. Any
//                        signed-in, email-verified user gets in, and the page
//                        limits its own content by access level. Use it on
//                        the dashboards and /portal, not on admin routes.
//   requireCapability  — "classroomTools" | "studentTracking" | "authoring"
//                        | "customContent" | "manageSeats"
//
// Two things changed in behaviour, both fixes:
//
// 1. The role is read live from Firestore rather than from the prop. App.js
//    fetches currentUserRole once in a useEffect and never refreshes it, so
//    when the Stripe webhook promoted someone mid-session the app kept using
//    the stale "unpaid" until a manual page reload. The onSnapshot in
//    useAccessLevel picks the change up as it lands.
//
// 2. A signed-in user who simply hasn't paid used to be dumped on
//    /unauthorized, which reads as "you did something wrong." They now go to
//    /choose-plan, which is where they were actually trying to get to.

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import useAccessLevel, { ACCESS } from "../hooks/useAccessLevel";

const PrivateRoute = ({
  children,
  allowedRoles = [],
  currentUserRole,
  allowFree = false,
  requireCapability = null,
}) => {
  const location = useLocation();
  const { loading, level, profile, can } = useAccessLevel();

  if (loading) {
    return <p style={{ padding: 24 }}>Loading…</p>;
  }

  if (level === ACCESS.ANONYMOUS) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (level === ACCESS.UNVERIFIED) {
    return <Navigate to="/verify-email" replace />;
  }

  // Live role first, prop as fallback so nothing breaks if a caller renders
  // this outside the usual App.js wiring.
  const role = profile?.role ?? currentUserRole ?? "";

  if (requireCapability && !can(requireCapability)) {
    return <Navigate to="/choose-plan" replace />;
  }

  const roleMatches =
    allowedRoles.length === 0 ||
    allowedRoles.some((allowed) => String(role).includes(allowed));

  if (roleMatches) return children;

  // No role match, but the route is marked allowFree — meaning the page
  // limits its own content and doesn't need a granted role to be safe.
  //
  // This used to read `allowFree && level === ACCESS.FREE`, which quietly
  // excluded everyone on a trial. A role string is only ever granted by the
  // Stripe webhook or claimSeat, so a trial user has no role to match AND
  // isn't FREE — they fell past both branches onto /unauthorized right after
  // finishing signup. Access level and role match answer different
  // questions; only the second one belongs in this check.
  if (allowFree) return children;

  if (level === ACCESS.FREE) {
    return <Navigate to="/choose-plan" replace />;
  }

  // Has access, but for a different state/grade/role than this route serves.
  return <Navigate to="/unauthorized" replace />;
};

export default PrivateRoute;
